// Copied verbatim from tab-portal src/lib/name-identification (qa c50f5268), the portal mirror of
// core-api src/utils/nameIdentification. Rules only: no AI pass. Keep the three in step.
// One change: the portal's server kill switch, isNameIdentificationDeriveEnabled, is left out
// because it reads an environment variable, and the engine reads none.
import { classifyName } from './classifyName'
import { PERSON_SUFFIXES } from './constants'
import { normalizeNameKey, tokenize } from './normalize'
import { parseBusinessName } from './parseBusinessName'
import { parsePersonName } from './parsePersonName'
import {
  ClassifyNameOptions,
  DerivedRecipientNameFields,
  NameOrder,
} from './types'

const emptyDerived = (): DerivedRecipientNameFields => ({
  RECIPIENT_FIRST_NAME: '',
  RECIPIENT_MIDDLE_NAME: '',
  RECIPIENT_LAST_NAME: '',
  RECIPIENT_SUFFIX: '',
  RECIPIENT_BUSINESS_NAME1: '',
  RECIPIENT_BUSINESS_NAME2: '',
})

/**
 * Derive 2026 split name columns from legacy combined RECIPIENT_NAME / NAME2.
 * Used by validationWorker (portal) and loadData backstop (core-api).
 */
export const deriveSplitNameFields = (
  name1: string | null | undefined,
  name2: string | null | undefined,
  order: NameOrder = 'surname-first',
  options: ClassifyNameOptions = {},
): DerivedRecipientNameFields & {
  classification: 'individual' | 'business'
} => {
  const primary = String(name1 ?? '').trim()
  const secondary = String(name2 ?? '').trim()

  if (!primary && !secondary) {
    return { ...emptyDerived(), classification: 'individual' }
  }

  const classification =
    options.classificationOverride ??
    classifyName(
      normalizeNameKey(primary || secondary) || primary || secondary,
      options,
    ).classification

  if (classification === 'business') {
    const parsed = parseBusinessName(primary, secondary)
    return {
      RECIPIENT_FIRST_NAME: '',
      RECIPIENT_MIDDLE_NAME: '',
      RECIPIENT_LAST_NAME: '',
      RECIPIENT_SUFFIX: '',
      RECIPIENT_BUSINESS_NAME1: parsed.businessName1,
      RECIPIENT_BUSINESS_NAME2: parsed.businessName2,
      classification,
    }
  }

  const parsed = parsePersonName(primary, order)
  return {
    RECIPIENT_FIRST_NAME: parsed.first,
    RECIPIENT_MIDDLE_NAME: parsed.middle,
    RECIPIENT_LAST_NAME: parsed.last,
    RECIPIENT_SUFFIX: parsed.suffix,
    RECIPIENT_BUSINESS_NAME1: '',
    RECIPIENT_BUSINESS_NAME2: secondary,
    classification,
  }
}

/** Legacy upload-only column keys per form (Layer 1+). */
export const LEGACY_NAME_COLUMNS_BY_FORM: Record<string, [string, string]> = {
  '1099-NEC': ['RECIPIENT_NAME', 'RECIPIENT_NAME2'],
  '1099-MISC': ['RECIPIENT_NAME', 'RECIPIENT_NAME2'],
  '1099-R': ['RECIPIENT_NAME', 'RECIPIENT_NAME2'],
  '1099-DIV': ['RECIPIENT_NAME', 'RECIPIENT_NAME2'],
  '1099-C': ['RECIPIENT_NAME', 'RECIPIENT_NAME2'],
  '1099-INT': ['RECIPIENT_NAME', 'RECIPIENT_NAME2'],
  '1099-B': ['RECIPIENT_NAME1', 'RECIPIENT_NAME2'],
  // E-file app only: no portal 1099-Q template, so the combined columns are its own (TS-8758).
  '1099-Q': ['RECIPIENT_NAME', 'RECIPIENT_NAME2'],
  '1099-K': ['PAYEE_NAME', 'PAYEE_NAME2'],
  '1098-T': ['STUDENT_NAME', 'STUDENT_NAME2'],
  // Form 1098 has one combined column, BORROWER_FULL_NAME, and no continuation
  // line; the second entry names a column no template carries, so it never
  // matches a mapping.
  '1098': ['BORROWER_FULL_NAME', 'BORROWER_FULL_NAME2'],
}

/**
 * Look up the legacy columns for a form type.
 *
 * Form type reaches us in several shapes depending on the caller: "1099-NEC"
 * from the wizard atom, "1099NEC" from the core-api form config key, and
 * lowercase variants from saved file metadata. Normalize to alphanumerics so
 * every spelling resolves to the same entry — a miss here silently skips the
 * Resolve Names step rather than erroring, so it must not be case- or
 * punctuation-sensitive.
 */
export const normalizeFormTypeKey = (formType: string): string =>
  String(formType ?? '')
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')

/**
 * Forms whose client template carries the combined name AND the split columns
 * side by side ("use this field only if you cannot provide a separated name").
 * Auto-map maps both, so the file-level rule below -- any mapped split column
 * switches derivation off -- would never parse the combined name. For these
 * forms the combined name is parsed per row, and only for a row whose split
 * name columns are all blank.
 */
const ROW_LEVEL_NAME_FALLBACK_FORMS = new Set(
  Object.keys(LEGACY_NAME_COLUMNS_BY_FORM).map(normalizeFormTypeKey),
)

export const usesRowLevelNameFallback = (formType: string): boolean =>
  ROW_LEVEL_NAME_FALLBACK_FORMS.has(normalizeFormTypeKey(formType))

const normalizeFormKey = normalizeFormTypeKey

const LEGACY_NAME_COLUMNS_NORMALIZED: Record<string, [string, string]> =
  Object.fromEntries(
    Object.entries(LEGACY_NAME_COLUMNS_BY_FORM).map(([key, value]) => [
      normalizeFormKey(key),
      value,
    ]),
  )

export const legacyNameColumnsForForm = (
  formType: string,
): [string, string] | null =>
  LEGACY_NAME_COLUMNS_NORMALIZED[normalizeFormKey(formType)] ?? null

/** Split DB columns that indicate the client already mapped 2026-style names. */
export const SPLIT_NAME_DB_COLUMNS = [
  'RECIPIENT_FIRST_NAME',
  'RECIPIENT_MIDDLE_NAME',
  'RECIPIENT_LAST_NAME',
  'RECIPIENT_SUFFIX',
  'RECIPIENT_BUSINESS_NAME1',
  'RECIPIENT_BUSINESS_NAME2',
] as const

export const STUDENT_SPLIT_NAME_DB_COLUMNS = [
  'STUDENT_FIRST_NAME',
  'STUDENT_MIDDLE_NAME',
  'STUDENT_LAST_NAME',
  'STUDENT_SUFFIX',
] as const

/**
 * Form 1098 split columns, in SPLIT_NAME_DB_COLUMNS order. The template names
 * the business lines BORROWER_COMPANY_NAME and RECIPIENT_BUSINESS_NAME2.
 */
export const BORROWER_1098_SPLIT_NAME_DB_COLUMNS = [
  'BORROWER_FIRST_NAME',
  'BORROWER_MIDDLE_NAME',
  'BORROWER_LAST_NAME',
  'BORROWER_SUFFIX',
  'BORROWER_COMPANY_NAME',
  'RECIPIENT_BUSINESS_NAME2',
] as const

export const splitNameDbColumnsForForm = (
  formType: string,
): readonly string[] => {
  const key = normalizeFormKey(formType)
  if (key === '1098T') return STUDENT_SPLIT_NAME_DB_COLUMNS
  if (key === '1098') return BORROWER_1098_SPLIT_NAME_DB_COLUMNS
  return SPLIT_NAME_DB_COLUMNS
}

const isPersonSuffixToken = (token: string): boolean => {
  const normalized = token.toUpperCase().replace(/\./g, '')
  return PERSON_SUFFIXES.has(normalized)
}

/** 1098-T line 2: generational suffix → STUDENT_SUFFIX; other text is not auto-filed. */
const apply1098TStudentLine2 = (
  columns: Record<string, string>,
  line2: string,
): { columns: Record<string, string>; unplacedLine2?: string } => {
  const trimmed = line2.trim()
  if (!trimmed) return { columns }

  const tokens = tokenize(trimmed)
  if (tokens.length === 1 && isPersonSuffixToken(tokens[0])) {
    const suffixToken = tokens[0]
    const existing = String(columns.STUDENT_SUFFIX ?? '').trim()
    return {
      columns: {
        ...columns,
        STUDENT_SUFFIX: existing
          ? `${existing} ${suffixToken}`.trim()
          : suffixToken,
      },
    }
  }

  return { columns, unplacedLine2: trimmed }
}

export type MappedSplitColumnsResult = {
  columns: Record<string, string>
  /** Legacy STUDENT_NAME2 text we do not map without a product rule (e.g. c/o). */
  unplacedLine2?: string
}

/**
 * Map derived recipient-shaped fields onto the split columns for this form.
 */
export const mapDerivedToSplitColumns = (
  formType: string,
  derived: DerivedRecipientNameFields,
): MappedSplitColumnsResult => {
  const key = normalizeFormKey(formType)
  if (key === '1098T') {
    const line2 = String(derived.RECIPIENT_BUSINESS_NAME2 ?? '').trim()
    const base = {
      STUDENT_FIRST_NAME: String(derived.RECIPIENT_FIRST_NAME ?? ''),
      STUDENT_MIDDLE_NAME: String(derived.RECIPIENT_MIDDLE_NAME ?? ''),
      STUDENT_LAST_NAME: String(derived.RECIPIENT_LAST_NAME ?? ''),
      STUDENT_SUFFIX: String(derived.RECIPIENT_SUFFIX ?? ''),
    }
    return apply1098TStudentLine2(base, line2)
  }
  if (key === '1098') {
    const columns: Record<string, string> = {}
    SPLIT_NAME_DB_COLUMNS.forEach((recipientColumn, index) => {
      columns[BORROWER_1098_SPLIT_NAME_DB_COLUMNS[index]] = String(
        derived[recipientColumn] ?? '',
      )
    })
    return { columns }
  }
  const columns: Record<string, string> = {}
  for (const column of SPLIT_NAME_DB_COLUMNS) {
    columns[column] = String(derived[column] ?? '')
  }
  return { columns }
}

/**
 * Map Fields names a form offers for the split columns when they differ from
 * the table's. 1099-K 2026 offers the client-template names (PAYEE_*, TS-8229);
 * core-api rewrites them to RECIPIENT_* on load, so a file that mapped them has
 * adopted the 2026 layout just as much as one that mapped RECIPIENT_* directly.
 */
const SPLIT_NAME_MAPPING_ALIASES_BY_FORM: Record<string, readonly string[]> = {
  // Taken from the template, which drops the payee prefix on the business lines.
  '1099-K': [
    'PAYEE_FIRST_NAME',
    'PAYEE_MIDDLE_NAME',
    'PAYEE_LAST_NAME',
    'PAYEE_SUFFIX',
    'BUSINESS_NAME1',
    'BUSINESS_NAME2',
  ],
}

/** Every mapping target that counts as a split name column for this form. */
export const splitNameMappingColumnsForForm = (
  formType: string,
): readonly string[] => [
  ...splitNameDbColumnsForForm(formType),
  ...(SPLIT_NAME_MAPPING_ALIASES_BY_FORM[
    Object.keys(SPLIT_NAME_MAPPING_ALIASES_BY_FORM).find(
      (key) => normalizeFormKey(key) === normalizeFormKey(formType),
    ) ?? ''
  ] ?? []),
]

/**
 * True when any mapped split-name column carries a non-blank value on this row.
 * Used to skip Resolve Names for rows the client already filed in 2026 layout.
 */
export const rowHasMappedSplitNameData = (
  row: Record<string, unknown>,
  sheetColumnByDbField: Record<string, string>,
  formType: string,
): boolean => {
  for (const dbField of splitNameMappingColumnsForForm(formType)) {
    const sheetCol = sheetColumnByDbField[dbField]
    if (!sheetCol) continue
    if (String(row?.[sheetCol] ?? '').trim()) return true
  }
  return false
}

const columnExists = (
  columnTypes: Record<string, string>,
  columnName: string,
): boolean =>
  Object.keys(columnTypes).some(
    (key) => key.toLowerCase() === columnName.toLowerCase(),
  )

/**
 * Legacy primary columns that are real columns on the form table by design, not
 * a sign the table was never migrated.
 *
 * 1099-K is the exception to the rule below: PAYEE_NAME and PAYEE_NAME2 exist on
 * `form1099K_2026` because the A2A e-file path reads them. Treating their
 * presence as "table not migrated" refused derivation for every 1099-K file, so
 * a file that named the payee only through those columns loaded with all six
 * split columns empty -- the e-file carried the name and the printed copy did
 * not (tabservice-forms#14).
 */
const LEGACY_COLUMNS_REAL_ON_TABLE = new Set(['PAYEE_NAME'])

/**
 * Derivation is safe only when the target table has all six split columns and
 * the legacy combined column is either absent (the usual upload-only virtual
 * field) or known to be a real column on that table.
 */
export const tableSupportsSplitNameDerivation = (
  columnTypes: Record<string, string>,
  legacyColumns: [string, string] | null,
  formType: string,
): boolean => {
  if (!legacyColumns || Object.keys(columnTypes).length === 0) return false

  const splitColumns = splitNameDbColumnsForForm(formType)
  const allSplitPresent = splitColumns.every((column) =>
    columnExists(columnTypes, column),
  )
  const legacyPrimarySafe =
    !columnExists(columnTypes, legacyColumns[0]) ||
    LEGACY_COLUMNS_REAL_ON_TABLE.has(legacyColumns[0].toUpperCase())
  return allSplitPresent && legacyPrimarySafe
}
